import { FormGroup } from '@angular/forms';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { isEqual } from 'lodash-es';
import { INCONSISTENT_SELECTION, INCONSISTENT_VALUE, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import {
    MyAllElemConsiglioAmministrazioneEntity,
    MyAllElemLiquidatoriEntity,
} from '../../../../models/interfaces/revisione.interface';
import {
    getNormeSpaMonisticoControl,
    getAmmControlloControl,
    checkCkNormeValue,
    normeSpaMonisticoCkHelper,
    normeSpaMonisticoRequiredHelper,
    setRequiredIfNormeActiveAndSecondFilled,
} from './helpers/norme-spa-monistico-form-validators.helper';

export const normeSpaMonisticoAssembleaTipoAmmRules: FormGroupRules = {
    my_CkSpmAssembAppBil: (form: FormGroup) => {
        normeSpaMonisticoRequiredHelper(form, 'my_NormeSpaMonistico.my_CkSpmAssembAppBil');
    },

    my_CkSpmAssembNominAmmin: (form: FormGroup) => {
        normeSpaMonisticoRequiredHelper(form, 'my_NormeSpaMonistico.my_CkSpmAssembNominAmmin');
    },

    my_CkSpmAssembNominOrgano: (form: FormGroup) => {
        normeSpaMonisticoRequiredHelper(form, 'my_NormeSpaMonistico.my_CkSpmAssembNominOrgano');
    },

    my_CkSpmAssembDetCompAmmin: (form: FormGroup) => {
        normeSpaMonisticoCkHelper(form, 'my_NormeSpaMonistico.my_CkSpmAssembDetCompAmmin', 'my_NormeSpaMonistico.my_CkSpmGratuitaCariche', 0);
    },

    my_SpmNoteFunzAssemb: (form: FormGroup) => {
        normeSpaMonisticoRequiredHelper(form, 'my_NormeSpaMonistico.my_SpmNoteFunzAssemb');
    },

    my_CkSpmMaggAmminSoci: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaMonistico.my_CkSpmMaggAmminSoci',
            'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione'
        );
    },

    my_CkSpmMetaAmminIndip: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaMonistico.my_CkSpmMetaAmminIndip',
            'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione'
        );
    },

    my_CkSpmAmminCarattMutual: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaMonistico.my_CkSpmAmminCarattMutual',
            'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione'
        );
    },

    my_CkSpmCompStatoInConsAmmin: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaMonistico.my_CkSpmCompStatoInConsAmmin',
            'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione'
        );
    },

    my_SpmNoteComposConsAmmin: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaMonistico.my_SpmNoteComposConsAmmin',
            'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione'
        );
    },

    my_CkSpmTipoAmministrazione: (form: FormGroup) => {
        const my_CkSpmTipoAmministrazioneControl = getNormeSpaMonisticoControl(form, 'my_CkSpmTipoAmministrazione');
        const my_CkSpmTipoAmministrazione: string = my_CkSpmTipoAmministrazioneControl.value;
        const my_AllElemConsiglioAmministrazione: MyAllElemConsiglioAmministrazioneEntity[] = getAmmControlloControl(
            form,
            'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione'
        ).value;
        const my_AllElemLiquidatori: MyAllElemLiquidatoriEntity[] = getAmmControlloControl(form, 'my_AllLiquidatori.my_AllElemLiquidatori').value;
        const errors = my_CkSpmTipoAmministrazioneControl.errors ?? {};

        addRemoveError(checkCkNormeValue(form) && my_CkSpmTipoAmministrazione === '1' && !my_AllElemConsiglioAmministrazione?.length, errors, 'my_CkSpmTipoAmministrazione', {
            status: 'Inserire il Consiglio di Amministrazione',
            message: 'Specificare il Consiglio di Amministrazione, in quanto si è scelto come tipo di amministrazione.',
        });

        addRemoveError(checkCkNormeValue(form) && my_CkSpmTipoAmministrazione === '1' && !isNilOrEmpty(my_AllElemLiquidatori), errors, 'my_CkSpmTipoAmministrazione', {
            status: 'Eliminare i Liquidatori',
            message: 'Si è scelto Consiglio di Amministrazione come tipo di amministrazione ma sono presenti ancora uno o più Liquidatori.',
        });

        addRemoveError(checkCkNormeValue(form) && my_CkSpmTipoAmministrazione === '3' && !my_AllElemLiquidatori?.length, errors, 'my_CkSpmTipoAmministrazione', {
            status: 'Inserire i Liquidatori',
            message: 'Specificare uno o più Liquidatori in quanto selezionati come tipo di amministrazione.',
        });

        addRemoveError(checkCkNormeValue(form) && my_CkSpmTipoAmministrazione === '3' && !isNilOrEmpty(my_AllElemConsiglioAmministrazione), errors, 'my_CkSpmTipoAmministrazione', {
            status: 'Eliminare il Consiglio di Amministrazione',
            message: 'Si è scelto Liquidatori come tipo di amministrazione ma sono presenti ancora componenti del Consiglio di Amministrazione.',
        });

        addRemoveError(checkCkNormeValue(form) && isNilOrEmpty(my_CkSpmTipoAmministrazione), errors, 'my_CkSpmTipoAmministrazione', {
            status: 'Occorre specificare il tipo di amministrazione',
        });

        my_CkSpmTipoAmministrazioneControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SpmScadenzaAmministratori: (form: FormGroup) => {
        const my_SpmScadenzaAmministratoriControl = getNormeSpaMonisticoControl(form, 'my_SpmScadenzaAmministratori');
        const my_CkSpmTipoAmministrazione: string = getNormeSpaMonisticoControl(form, 'my_CkSpmTipoAmministrazione').value;
        const errors = my_SpmScadenzaAmministratoriControl.errors ?? {};

        addRemoveError(
            checkCkNormeValue(form) && my_SpmScadenzaAmministratoriControl.value === '99' && my_CkSpmTipoAmministrazione !== '3',
            errors,
            'my_SpmScadenzaAmministratori',
            { status: INCONSISTENT_SELECTION }
        );

        addRemoveError(
            checkCkNormeValue(form) && my_SpmScadenzaAmministratoriControl.value !== '99' && my_CkSpmTipoAmministrazione === '3',
            errors,
            'my_SpmScadenzaAmministratori',
            { status: INCONSISTENT_VALUE }
        );

        addRemoveError(checkCkNormeValue(form) && isNilOrEmpty(my_SpmScadenzaAmministratoriControl.value), errors, 'my_SpmScadenzaAmministratori', {
            status: REQUIRED_STATUS,
        });

        my_SpmScadenzaAmministratoriControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};
